import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowRight } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, attempts: 0 });
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    if (!sessionId) {
      setState({ status: "error", data: null, attempts: 0 });
      return;
    }

    let attempts = 0;
    const poll = async () => {
      if (cancelRef.current) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed", data, attempts });
          return;
        }
        attempts += 1;
        setState({ status: "pending", data, attempts });
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, attempts });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (_err) {
        setState({ status: "error", data: null, attempts });
      }
    };
    poll();
    return () => {
      cancelRef.current = true;
    };
  }, [sessionId]);

  return (
    <main className="min-h-[70vh] bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl bg-white border border-neutral-200 p-8 md:p-10 shadow-sm">
          {state.status === "paid" ? (
            <PaidView data={state.data} />
          ) : state.status === "failed" ? (
            <FailedView />
          ) : state.status === "timeout" ? (
            <TimeoutView />
          ) : state.status === "error" ? (
            <ErrorView />
          ) : (
            <PendingView attempts={state.attempts} />
          )}
        </div>
      </div>
    </main>
  );
}

function PendingView({ attempts }) {
  return (
    <>
      <Loader2 className="mx-auto text-green-600 animate-spin" size={40} />
      <h1 className="mt-4 text-2xl font-extrabold text-neutral-900">Finalising your order…</h1>
      <p className="mt-2 text-sm text-neutral-600">
        We’re confirming your payment with Stripe. This usually takes a couple of seconds.
      </p>
      <p className="mt-3 text-xs text-neutral-400">Attempt {attempts + 1}</p>
    </>
  );
}

function PaidView({ data }) {
  return (
    <>
      <CheckCircle2 className="mx-auto text-green-600" size={48} />
      <h1 className="mt-4 text-2xl md:text-3xl font-extrabold text-neutral-900">
        Order confirmed 🎉
      </h1>
      <p className="mt-2 text-neutral-600">
        Thanks for choosing Fit Kiwi. Your first delivery is on its way!
      </p>
      {data?.plan_name && (
        <div className="mt-6 inline-flex flex-col items-center rounded-xl bg-green-50 border border-green-200 px-5 py-3">
          <p className="text-xs uppercase tracking-wider text-green-700 font-bold">Plan</p>
          <p className="mt-0.5 font-bold text-neutral-900">{data.plan_name}</p>
          {data.amount != null && (
            <p className="mt-1 text-sm text-neutral-600">
              {(data.currency || "nzd").toUpperCase()} {(data.amount / 100).toFixed(2)}
            </p>
          )}
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/account">
          <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-6">
            View My Orders <ArrowRight size={16} className="ml-1" />
          </Button>
        </Link>
        <Link to="/menu">
          <Button variant="outline" className="rounded-full border-neutral-300 px-6">See this week’s menu</Button>
        </Link>
      </div>
    </>
  );
}

function FailedView() {
  return (
    <>
      <XCircle className="mx-auto text-red-500" size={48} />
      <h1 className="mt-4 text-2xl font-extrabold text-neutral-900">Payment didn’t complete</h1>
      <p className="mt-2 text-neutral-600">No charge was made. Please try again.</p>
      <Link to="/plans" className="inline-block mt-6">
        <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-6">Back to Plans</Button>
      </Link>
    </>
  );
}

function TimeoutView() {
  return (
    <>
      <Clock className="mx-auto text-amber-500" size={48} />
      <h1 className="mt-4 text-2xl font-extrabold text-neutral-900">Still processing…</h1>
      <p className="mt-2 text-neutral-600">
        This is taking longer than usual. Check My Orders in a minute — we’ll email once it’s confirmed.
      </p>
      <Link to="/account" className="inline-block mt-6">
        <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-6">Go to My Orders</Button>
      </Link>
    </>
  );
}

function ErrorView() {
  return (
    <>
      <XCircle className="mx-auto text-red-500" size={48} />
      <h1 className="mt-4 text-2xl font-extrabold text-neutral-900">Something went wrong</h1>
      <p className="mt-2 text-neutral-600">We couldn’t look up your payment. Please contact support.</p>
      <Link to="/contact" className="inline-block mt-6">
        <Button className="rounded-full bg-green-600 hover:bg-green-700 text-white px-6">Contact us</Button>
      </Link>
    </>
  );
}
